import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type FieldToolDialog, type FieldToolEvent, type FieldToolsView } from '../../api/client';
import { Button, Chip, Notice } from '../../components/ui';
import { clockTime } from '../../lib/format';

/**
 * The gateway's watch and pick-up test (3025), run here instead of `--console` on the gateway server
 * (1.13.0, Teampace 3131): turn the gateway's field test tools on for the test company, watch
 * extensions from one of its lines, and pick a watched call up with Replaces. The calls show live
 * (d1, d2…, as the console numbers them) and the log reads like the console's events.jsonl; saving
 * the test keeps the log from when this panel opened.
 */

const MINUTES: [number, string][] = [
  [30, '30 minutes'],
  [60, '1 hour'],
  [120, '2 hours'],
  [240, '4 hours']
];

const STATE: Record<string, string> = { trying: 'Trying', proceeding: 'Ringing', early: 'Ringing', confirmed: 'Talking', terminated: 'Ended' };

/** "sip:+14105550100@acme" → "+14105550100". */
const who = (uri: string) => uri.replace(/^sips?:/, '').replace(/@.*$/, '') || uri;

/** One log line in a few words: what it was, and the bits that matter for the test. */
function logLine(e: FieldToolEvent): string {
  const d = e.data;
  const s = (k: string) => (d[k] === undefined || d[k] === null ? '' : String(d[k]));
  switch (e.kind) {
    case 'on':
      return `Tools on for ${s('minutes')} minutes`;
    case 'off':
      return `Tools off (${s('reason')})${Number(d.watchesStopped) ? `, ${s('watchesStopped')} watch(es) stopped` : ''}`;
    case 'watch':
      return `Watching ${s('ext')}`;
    case 'unwatch':
      return `Stopped watching ${s('ext')}`;
    case 'watch-failed':
      return `Watching ${s('ext')} failed: ${s('error')}`;
    case 'dialogs': {
      const nums = Array.isArray(d.numbers) ? (d.numbers as number[]).map((n) => `d${n}`).join(', ') : '';
      return `${s('ext')}: ${Array.isArray(d.dialogs) ? d.dialogs.length : 0} call(s)${nums ? ` (${nums})` : ''} · ${s('state')} · version ${s('version')} · ${s('subscription')}`;
    }
    case 'pickup':
      return `Picked up d${s('dialog')} → ${s('target')}${d.swap ? ' (swap)' : ''} · Replaces: ${s('replaces')}`;
    case 'pickup-failed':
      return `Pick-up of d${s('dialog')} failed: ${s('error')}`;
    default:
      return `${e.kind} ${JSON.stringify(d)}`;
  }
}

export function GatewayRun({ onStart }: { onStart: (since: number) => void }) {
  const [view, setView] = useState<FieldToolsView | null>(null);
  const [log, setLog] = useState<FieldToolEvent[]>([]);
  const next = useRef(0);
  const started = useRef(false);
  const [company, setCompany] = useState('');
  const [minutes, setMinutes] = useState(120);
  const [line, setLine] = useState('');
  const [ext, setExt] = useState('');
  const [to, setTo] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const v = await api.fieldTools(company || undefined, next.current || undefined);
      setView(v);
      if (v.tools) {
        // The log from when this panel opened is what's saved with the result.
        if (!started.current) {
          started.current = true;
          onStart(v.tools.next);
        }
        const fresh = v.tools.log.filter((e) => e.seq >= next.current);
        if (fresh.length) setLog((l) => [...l, ...fresh].slice(-300));
        next.current = v.tools.next;
      }
      if (!company && v.company) setCompany(v.company.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [company, onStart]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 2000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!line && view?.lines.length) setLine(view.lines[0]!.person);
  }, [view, line]);

  async function act(what: string, fn: () => Promise<unknown>) {
    setBusy(what);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }

  if (!view) return <p className="muted small-print">Asking the gateway…</p>;
  const g = view.gateway;
  if (!g.set) return <Notice tone="warn">The call gateway isn’t set up (Stitch Admin → System → Call gateway).</Notice>;
  if (!g.supported)
    return (
      <Notice tone="warn">
        {g.error ? `The gateway didn’t answer: ${g.error}` : `This gateway${g.version ? ` (${g.version})` : ''} has no field test tools: it needs the 1.13.0 installer.`} Until then the console way works
        (services/gateway/FIELD-TEST.md).
      </Notice>
    );
  const tools = view.tools;
  const on = Boolean(tools?.on);
  const lineName = (person: string) => {
    const l = view.lines.find((x) => x.person === person);
    return l ? `${l.extension}${l.name ? ` (${l.name})` : ''}` : 'a line';
  };

  return (
    <section className="gateway-run" aria-label="Field test tools">
      <h4 className="field-test-h">Field test tools</h4>
      {error && <Notice tone="crit">{error}</Notice>}
      {!on ? (
        <div className="gateway-run-row">
          <div className="field">
            <label htmlFor="gw-company">Test company</label>
            <select id="gw-company" className="input" value={company} onChange={(e) => setCompany(e.target.value)}>
              {view.companies.length === 0 && <option value="">None inside the write fence</option>}
              {view.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="gw-minutes">For</label>
            <select id="gw-minutes" className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {MINUTES.map(([m, label]) => (
                <option key={m} value={m}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <Button size="small" variant="primary" disabled={!company || Boolean(busy)} onClick={() => void act('on', () => api.setFieldTools({ on: true, minutes, company }))}>
            Turn on
          </Button>
          <small className="muted grow">Off until you turn them on; they go off by themselves when the time’s up. Only the test company’s own lines can watch or pick up.</small>
        </div>
      ) : (
        <>
          <div className="gateway-run-row">
            <Chip tone="ok">{`On for ${view.company?.name ?? 'the test company'}${tools?.until ? ` until ${clockTime(tools.until)}` : ''}`}</Chip>
            <Button size="small" disabled={Boolean(busy)} onClick={() => void act('off', () => api.setFieldTools({ on: false }))}>
              Turn off
            </Button>
          </div>
          <form
            className="gateway-run-row"
            aria-label="Watch an extension"
            onSubmit={(e) => {
              e.preventDefault();
              void act('watch', () => api.fieldToolWatch(line, ext.trim()));
            }}
          >
            <div className="field">
              <label htmlFor="gw-line">Watch from</label>
              <select id="gw-line" className="input" value={line} onChange={(e) => setLine(e.target.value)}>
                {view.lines.length === 0 && <option value="">Nobody at {view.company?.name ?? 'the company'} is signed in with calls in the app</option>}
                {view.lines.map((l) => (
                  <option key={l.person} value={l.person}>
                    {`${l.extension}${l.name ? ` · ${l.name}` : ''}${l.state === 'registered' ? '' : ` · ${l.state}`}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="gw-ext">Extension</label>
              <input id="gw-ext" className="input" inputMode="numeric" maxLength={15} value={ext} placeholder="201" onChange={(e) => setExt(e.target.value.replace(/\D/g, ''))} />
            </div>
            <Button size="small" type="submit" disabled={!line || !ext || Boolean(busy)}>
              Watch
            </Button>
          </form>
          {tools && tools.watches.length > 0 && (
            <ul className="plain-list gateway-run-watches" aria-label="Watching">
              {tools.watches.map((w) => (
                <li key={`${w.person}/${w.ext}`}>
                  <span>{`Watching ${w.ext} from ${lineName(w.person)}`}</span>{' '}
                  <button type="button" className="link-btn small-print" disabled={Boolean(busy)} onClick={() => void act('unwatch', () => api.fieldToolUnwatch(w.person, w.ext))}>
                    Stop watching {w.ext}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <ol className="plain-list gateway-run-calls" aria-label="Watched calls">
            {(tools?.dialogs ?? []).length === 0 ? (
              <li className="muted small-print">No calls yet: make or take one on a watched extension.</li>
            ) : (
              [...(tools?.dialogs ?? [])].reverse().map((d) => <WatchedCall key={d.n} d={d} busy={Boolean(busy)} to={to[d.n] ?? ''} setTo={(v) => setTo((x) => ({ ...x, [d.n]: v }))} act={act} />)
            )}
          </ol>
        </>
      )}
      {log.length > 0 && (
        <details className="gateway-run-log" open={on}>
          <summary>{`Log (${log.length} line${log.length === 1 ? '' : 's'}: saved with the result)`}</summary>
          <ol className="plain-list mono small-print" aria-label="Field test tools log">
            {[...log].reverse().map((e) => (
              <li key={e.seq}>
                <span className="muted">{clockTime(e.at)}</span> {logLine(e)}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

function WatchedCall({ d, busy, to, setTo, act }: { d: FieldToolDialog; busy: boolean; to: string; setTo: (v: string) => void; act: (what: string, fn: () => Promise<unknown>) => Promise<void> }) {
  const live = d.state !== 'terminated';
  return (
    <li className="gateway-run-call" aria-label={`d${d.n}`}>
      <div className="gateway-run-call-head">
        <strong>{`d${d.n}`}</strong>
        <span>{d.ext}</span>
        <Chip small tone={d.state === 'confirmed' ? 'ok' : d.state === 'terminated' ? 'neutral' : 'brand'}>
          {STATE[d.state] ?? d.state}
        </Chip>
        <span className="muted">{d.direction === 'initiator' ? 'Placed by it' : 'To it'}</span>
        <span>
          Other side: {d.remoteName ? `${d.remoteName} ` : ''}
          <span className="mono">{who(d.remote)}</span>
        </span>
      </div>
      <small className="muted mono">{`Call id ${d.callId} · its tag ${d.localTag} · the phone system’s ${d.remoteTag}`}</small>
      {live && (
        <div className="gateway-run-row">
          <Button size="small" variant="primary" disabled={busy} onClick={() => void act('pickup', () => api.fieldToolPickup({ person: d.person, dialog: d.n }))}>
            Pick up here
          </Button>
          <Button size="small" disabled={busy} onClick={() => void act('pickup', () => api.fieldToolPickup({ person: d.person, dialog: d.n, swap: true }))}>
            Pick up (swap)
          </Button>
          <input className="input small" aria-label={`Pick up d${d.n} to this number`} inputMode="tel" maxLength={33} placeholder="Other party’s number" value={to} onChange={(e) => setTo(e.target.value.replace(/[^0-9*#+]/g, ''))} />
          <Button size="small" disabled={busy || !to} onClick={() => void act('pickup', () => api.fieldToolPickup({ person: d.person, dialog: d.n, target: to }))}>
            Pick up to…
          </Button>
        </div>
      )}
    </li>
  );
}
